import { ResizableTableCellHeaderText } from '@/_shared/components/resizable-table/components/cell-header-text/index.js';
import { stopPropagation } from '@/_shared/tools/event-prevention.js';
import { Button, type ButtonProps, Tooltip } from '@fluentui/react-components';
import { ChevronUpDownRegular, FilterAddFilled, FilterRegular } from '@fluentui/react-icons';
import { ArrowDownRegular, ArrowUpRegular } from '@fluentui/react-icons';
import type { Column } from '@tanstack/react-table';
import { type MouseEvent, type MouseEventHandler, type ReactNode, useCallback } from 'react';
import { useStyles } from './styles.js';

type DataTableHeaderButtonProps<TData, TValue> = Omit<ButtonProps, 'appearance' | 'className' | 'onClick' | 'title'> & {
    column: Column<TData, TValue>;
    title: ReactNode;
    onClick: MouseEventHandler<HTMLButtonElement>;
    closePopover: () => void;
};

export function DataTableHeaderButton<TData, TValue>({
    column,
    title,
    closePopover,
    ...props
}: DataTableHeaderButtonProps<TData, TValue>) {
    const styles = useStyles();

    const sortingAsc = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            stopPropagation(event);
            closePopover();
            column.toggleSorting(false);
        },
        [closePopover, column.toggleSorting]
    );

    const sortingDesc = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            stopPropagation(event);
            closePopover();
            column.toggleSorting(true);
        },
        [closePopover, column.toggleSorting]
    );

    const isFiltered = column.getIsFiltered();

    return (
        <>
            <Button appearance="transparent" className={styles.triggerButton} {...(props as ButtonProps)}>
                <ResizableTableCellHeaderText>{title}</ResizableTableCellHeaderText>

                {isFiltered ? <FilterAddFilled className={styles.isFiltered} /> : <FilterRegular />}
            </Button>

            {column.getIsSorted() === 'asc' ? (
                <Tooltip content="Sortiert aufsteigend" relationship="label" positioning="below-start">
                    <Button
                        className={styles.sortingButton}
                        appearance="subtle"
                        icon={{
                            className: styles.sortingButtonIcon,
                            children: <ArrowUpRegular />,
                        }}
                        onClick={sortingDesc}
                    />
                </Tooltip>
            ) : column.getIsSorted() === 'desc' ? (
                <Tooltip content="Sortiert absteigend" relationship="label" positioning="below-start">
                    <Button
                        className={styles.sortingButton}
                        appearance="subtle"
                        icon={{
                            className: styles.sortingButtonIcon,
                            children: <ArrowDownRegular />,
                        }}
                        onClick={sortingAsc}
                    />
                </Tooltip>
            ) : (
                <Tooltip content="Nicht sortiert" relationship="label" positioning="below-start">
                    <Button
                        className={styles.sortingButton}
                        appearance="subtle"
                        icon={{
                            className: styles.sortingButtonIcon,
                            children: <ChevronUpDownRegular />,
                        }}
                        onClick={sortingDesc}
                    />
                </Tooltip>
            )}
        </>
    );
}
